import { useRuntimeVariables } from 'app-kit'
import rawAxios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { AuthInactivityMonitor } from 'features/auth/AuthInactivityMonitor'
import { doesHaveRequiredPermissionsBase } from 'features/auth/doesHaveRequiredPermissionsBase'
import { GeckoAuthContext } from 'features/auth/geckoAuthContext'
import { RolodexPermissions } from 'features/auth/permissions/rolodexPermissionsList'
import { useRolodexPermissionsUserMetadata } from 'features/auth/permissions/useRolodexPermissionsUserMetadata'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { AuthState, computePermissions } from './helper'
import { useKeycloak } from './KeycloakProvider'

/**
 * A component which handles Keycloak authentication, providing a GeckoAuthContext
 * to the rest of the app
 * @param children        The rest of the app (this component is a simple wrapper)
 */
export const KeycloakAuthContext: FC<{
  children: ReactNode
}> = ({ children }) => {
  const keycloak = useKeycloak()
  const { ROLODEX_URL } = useRuntimeVariables()
  const [validRefreshToken, setValidRefreshToken] = useState<boolean>(true)
  const [authState, setAuthState] = useState(new AuthState())

  // TODO: validate this works in keycloak
  const exchangeRefreshTokenAlias = keycloak.exchangeRefreshToken
  const attemptGetAccessToken = useCallback(() => {
    const tokenExchangePromise = exchangeRefreshTokenAlias()
    if (tokenExchangePromise) {
      tokenExchangePromise.catch(() => {
        setValidRefreshToken(false)
      })
    }
  }, [exchangeRefreshTokenAlias])

  useEffect(() => {
    if (!keycloak.user?.access_token || !keycloak.user.expires_at) {
      throw new Error('Keycloak user requires access_token and expires_at')
    }
    setValidRefreshToken(true)
    setAuthState({
      authorization: `Bearer ${keycloak.user.access_token}`,
      tokenExpiration: new Date(keycloak.user.expires_at * 1000),
    })
  }, [keycloak, keycloak.user])

  const authorizedAxios = useMemo(() => {
    const newAxios = rawAxios.create()
    newAxios.interceptors.request.use((requestConfig: AxiosRequestConfig) => {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: authState.authorization ?? null,
        } as AxiosRequestHeaders,
      }
    })
    return newAxios
  }, [authState.authorization])

  // Can't put this in `useRolodexPermissions` because that depends on this provider
  const { data: currentUserMetadata, mutate: refreshCurrentUser } =
    useRolodexPermissionsUserMetadata(
      authorizedAxios,
      authState.authorization,
      ROLODEX_URL
    )

  useEffect(() => {
    if (!currentUserMetadata) return
  }, [currentUserMetadata])

  const { permissions } = useMemo(() => {
    // Need to wait for the mask to be set so we don't return inaccurate permissions
    if (!currentUserMetadata) {
      return { permissions: new Set<string>(), permissionTags: {} }
    }
    return computePermissions(currentUserMetadata)
  }, [currentUserMetadata])

  const hasPermissions = useCallback(
    (toCheck: RolodexPermissions[]) =>
      doesHaveRequiredPermissionsBase(permissions, toCheck),
    [permissions]
  )

  const logout = React.useCallback(() => keycloak.signOut(), [keycloak])

  // Add state for showPermissionGates
  const [showPermissionGates, setShowPermissionGates] = useState<boolean>(false)

  return (
    <>
      <AuthInactivityMonitor
        tokenExpiration={authState.tokenExpiration}
        validRefreshToken={validRefreshToken}
        attemptGetAccessToken={attemptGetAccessToken}
        redirectToLogin={() => keycloak.sendToKeycloakLogin()}
      />
      <GeckoAuthContext.Provider
        value={{
          // Auth
          authorization: authState.authorization,
          authorizedAxios,
          isAuthenticated: !!authState.authorization,
          // User
          currentUser: currentUserMetadata,
          refreshCurrentUser,
          // Permissions
          permissions,
          hasPermissions,
          // Permissions V2
          user: {
            email: keycloak.user?.profile.email ?? '',
            name: keycloak.user?.profile.name ?? '',
            familyName: keycloak.user?.profile.family_name ?? '',
            givenName: keycloak.user?.profile.given_name ?? '',
            picture: keycloak.user?.profile.picture ?? '',
          },
          logout: logout,
          // Add missing context properties
          showPermissionGates,
          setShowPermissionGates,
        }}>
        {children}
      </GeckoAuthContext.Provider>
    </>
  )
}
